import { Component, HostListener, Input, OnChanges, OnInit, SecurityContext, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DomSanitizer } from "@angular/platform-browser";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { DocumentService } from "@tylertech/attachments-module";
import { BusyIndicatorComponent } from "@tylertech/common-components-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { SecurityService, ToastService} from "@tylertech/core-module";
import { IMenuOption } from "@tylertech/forge";
import { ForgeButtonModule, ForgeIconModule, ForgeMenuModule, ForgeScaffoldModule, ForgeSplitButtonModule } from "@tylertech/forge-angular";
import { ShellService } from "@tylertech/incode-module";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { Utilities } from "@tylertech/shared-module";
import { DocumentSearchCriteria, ITcmModel, IViewMetadata, PropertyMetadata, TCMAttachmentViewMetadata, } from "@tylertech/tyler-api-types";
import { RepositoryFormData } from "../../interfaces";
import { MetadataActionService, MetadataEventService, MetadataIframeService, MetadataPropertyService, MetadataService } from "../../services";


@Component({
    selector: "metadata-attachments",
    templateUrl: "./metadata.attachments.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeButtonModule,
        ForgeIconModule,
        ForgeMenuModule,
        ForgeScaffoldModule,
        ForgeSplitButtonModule,
    ]
})
export class MetadataAttachmentsComponent implements OnChanges, OnInit
{
    private readonly domSanitizer = inject(DomSanitizer);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private readonly $metadataIframeService = inject(MetadataIframeService);
    private readonly $documentApiService = inject(DocumentService);
    private readonly $toastService = inject(ToastService);
    private readonly $odataFilterFactoryService = inject(ODataFilterFactoryService);
    private readonly $securityService = inject(SecurityService);
    private readonly $shellService = inject(ShellService);
    private readonly $metadataEventService = inject(MetadataEventService);
    @Input() title: string;

    @Input() view: IViewMetadata;

    gridData: any[] = [];

    rowArray: number[];

    hashId: number;

    @Input() datacontroller: RepositoryFormData;

    @Input() model: any;

    loading: boolean = true;

    fetchingUrl: boolean = false;

    attachmentsUpdated: any;

    url: string;

    lastViewerUrl: string | null;

    tcmModel: ITcmModel;

    hasTcmPermission: boolean = true;

    compactMode: boolean = false;

    compoundOptions: IMenuOption[] = [];

    currentCompoundOption: string = "Download";

    constructor()
    {
        this.$metadataEventService.onRecalculateWorking
            .pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                if (args.Recalculating || this.tcm)
                    return;
                if (this.view["ExternalUrlProperty"] && this.hasModelUrlChanged)
                    this.setUrl();
                else if ((this.model.ViewerUrl || this.lastViewerUrl) &&
                    this.model.ViewerUrl !== this.lastViewerUrl)
                    this.setUrl();
            });
    }

    get tcm(): boolean
    {
        return this.view.IsTCMAttachmentView;
    }

    ngOnInit(): void
    {
        this.compactMode = this.$securityService.IsCompactMode;

        this.compoundOptions.push(
            <IMenuOption>{
                value: "Download",
                label: "Download"
            },
            <IMenuOption>{
                value: "Email",
                label: "Email"
            }
        );
    }

    menuOpened(e): void
    {
        e.target.activateFirstOption();
    }

    get allowEmail(): boolean
    {
        return !Utilities.IsNullOrUndefined(this.model.ReferenceId);
    }

    ngOnChanges(): void
    {
        this.setUrl();
    }

    get showViewer(): boolean
    {
        return !this.tcm || this.hasTcmPermission;
    }

    updateIframe(): void
    {
        requestAnimationFrame(() =>
        {
            const iframe = document.getElementById('embeddedPage') as HTMLIFrameElement;
            iframe.contentWindow.location.replace(this.url);
        });
    }

    setUrl(): void
    {
        const self = this;
        if (!self.tcm)
        {
            if (self.model.ViewerUrl)
            {
                this.lastViewerUrl = self.model.ViewerUrl;
                let url = atob(self.model.ViewerUrl);
                url = decodeURIComponent(url);

                if (url.startsWith(`blob:${window.location.origin}`))
                {
                    fetch(url).then(res => res.blob())
                        .then(blob =>
                        {
                            const link = document.createElement("a");
                            link.href = window.URL.createObjectURL(blob);
                            document.body.appendChild(link);
                            link.setAttribute("type", "hidden");
                            link["download"] = "test.pdf";
                            window.URL.revokeObjectURL(url);
                            self.url = link.href + "#view=FitH";
                            self.loading = false;
                            this.updateIframe();
                        }, () =>
                        {
                            self.url = "about:blank";
                            self.loading = false;
                            this.updateIframe();
                        })
                        .finally(() =>
                        {
                            self.$metadataActionService.docViewerLoadHandler();
                        });
                }
                else if (url.startsWith('data'))
                {
                    this.url = url;
                    this.loading = false;
                    this.updateIframe();
                }
                else
                {
                    this.loading = false;
                }
            }
            else if (self.view["ExternalUrlProperty"])
            {
                self.url = this.sanitizedUrl;
                self.loading = false;
                this.hashId = this.$metadataIframeService.hashExternalUrl(this.urlFromModel);
                this.updateIframe();
            }
            else
            {
                this.lastViewerUrl = null;
                self.url = "about:blank";
                self.loading = false;
                this.updateIframe();
            }
            self.showConfirmationMessage();
            return;
        }
        self.loading = true;
        if (
            "TcmUrl" in self.view &&
            !Utilities.IsNullOrUndefinedOrEmpty((<TCMAttachmentViewMetadata>self.view).TcmUrl)
        )
        {
            self.hasTcmPermission = !Utilities.IsNullOrUndefinedOrEmpty((<TCMAttachmentViewMetadata>self.view).TcmUrl);
            self.url = self.domSanitizer.sanitize(SecurityContext.URL, (<TCMAttachmentViewMetadata>self.view).TcmUrl);
            self.loading = false;
            this.updateIframe();
        }
        else if (!Utilities.IsNullOrUndefined(self.model.Metadata))
        {
            if (this.fetchingUrl)
                return;
            const criteria = <DocumentSearchCriteria>{
                EntityId: self.$metadataService.DependentModuleValue(
                    self.view.ParentColumnName,
                    null,
                    self.model["$$Parent"]
                ),
                EntityType: self.view.ViewMetaData.TypeName,
                Entity: self.model,
            };
            this.$documentApiService
                .GetTcm(criteria)
                .then(x => x.Result)
                .then((url: string) =>
                {
                    self.hasTcmPermission = !Utilities.IsNullOrUndefinedOrEmpty(url);
                    self.url = self.domSanitizer.sanitize(SecurityContext.URL, url);
                    self.loading = false;
                    this.updateIframe();
                })
                .catch(_ =>
                {
                    self.$toastService.showToast("There was a problem accessing TCM.");
                })
                .finally(() => self.loading = false);
        }
        else if (!Utilities.IsNullOrUndefined(self.model.ViewerUrl))
        {
            self.hasTcmPermission = !Utilities.IsNullOrUndefinedOrEmpty(self.model.ViewerUrl);
            self.url = self.domSanitizer.sanitize(SecurityContext.URL, self.model.ViewerUrl);
            self.loading = false;
            this.updateIframe();
        }
        else
        {
            let criteria: DocumentSearchCriteria;
            if (self.model.CMDocumentType)
            {
                criteria = <DocumentSearchCriteria>{
                    EntityId: self.model.CMParentId,
                    EntityType: self.model.CMDocumentType,
                    Mode: self.model.CMViewerMode
                };
            }
            else
            {
                const viewFilter = self.$odataFilterFactoryService.parseOdataFilter(self.view.ConstantFilter);
                const viewerMode = !Utilities.IsNullOrUndefined(viewFilter) ? viewFilter["CMViewerMode"] : 0;
                criteria = <DocumentSearchCriteria>{
                    EntityId: self.$metadataService.DependentModuleValue(self.view.ParentColumnName, null, self.model),
                    EntityType: self.view.ViewMetaData.TypeName,
                    Entity: self.$metadataService.DependentModuleValue(self.view.SubPropertyName, null, self.model),
                    Mode: viewerMode
                };
            }
            criteria.OptionalCollection = self.model.SerializedRowState === "Added" ? self.datacontroller : null;
            this.fetchingUrl = true;
            this.$documentApiService
                .GetTcm(criteria)
                .then(x => x.Result)
                .then((url: string) =>
                {
                    self.hasTcmPermission = !Utilities.IsNullOrUndefinedOrEmpty(url);
                    self.url = self.domSanitizer.sanitize(SecurityContext.URL, url);
                    this.updateIframe();
                })
                .catch(_ =>
                {
                    self.$toastService.showToast("There was a problem accessing TCM.");
                })
                .finally(() =>
                {
                    self.loading = false;
                    self.fetchingUrl = false;
                });
        }
    }

    private _sanitizedUrl: string;
    private _rawUrl: string;
    get sanitizedUrl(): string
    {
        if (this.hasModelUrlChanged)
            this._sanitizedUrl = null;

        if (this._sanitizedUrl)
            return this._sanitizedUrl;

        this._rawUrl = this.urlFromModel;
        if (Utilities.IsNullOrUndefinedOrEmpty(this._rawUrl)) return null;
        this._sanitizedUrl = this.domSanitizer.sanitize(SecurityContext.URL, this._rawUrl);
        return this._sanitizedUrl;
    }

    get hasModelUrlChanged(): boolean
    {
        return this.urlFromModel !== this._rawUrl;
    }

    get urlFromModel(): string
    {
        return this.$metadataService.EvaluateString(this.view["ExternalUrlProperty"], null, this.model, null, true);
    }

    get hideFooter(): boolean
    {
        return this.view["HideFooter"] ?? false;
    }

    download(): void
    {
        const link = document.createElement("a");
        link.href = this.url;
        document.body.appendChild(link);
        link.setAttribute("type", "hidden");
        link["download"] = this.model.FileName;
        link.click();
    }

    compoundAction(option?: any): void
    {
        if (option)
            this.currentCompoundOption = option.detail.value;
        if (this.currentCompoundOption === "Email")
            this.email();
        else
            this.download();
    }

    email(): void
    {
        let propertyMetadata = <PropertyMetadata>{
            Name: "ShowEmailPromptRecalc",
            RecalculateOnChange: ["EmailAddresses", "HasEmailList", "EmailList", "EmailLabel", "EmailSubject", "EmailBody"],
            RecalculateWithParent: false,
            RecalculateToIgnore: ["ViewerUrl"],
            RecalculateDoNotSend: ["ViewerUrl"],
        };

        this.$metadataPropertyService.recalculateProperties(
            this.model.$$TypeName,
            propertyMetadata,
            this.model,
            this.model["$$Parent"],
            this.model[propertyMetadata.Name],
            undefined,
            true,
            true,
            this.datacontroller
        );
    }

    close(): void
    {
        window.URL.revokeObjectURL(decodeURIComponent(atob(this.model.ViewerUrl)));
        window.URL.revokeObjectURL(this.url);
        if (this.$shellService.isEmbedded())
        {
            this.$shellService.closeSelfFromShell("");
        }
        else
        {
            window.close();
        }
    }

    showConfirmationMessage(): void
    {
        if (!this.model.SaveConfirmationMessage)
            return;

        if (this.model.MessageAsToast)
            this.$toastService.showToast(decodeURIComponent(this.model.SaveConfirmationMessage));
        else
            this.$commonDialogsService.showAlertMessage(this.model.SaveConfirmationMessage, "Confirmation");
    }

    @HostListener("window:beforeunload", ["$event"])
    onWindowBeforeUnload($event: BeforeUnloadEvent): void
    {
        const propertyMetadata = <PropertyMetadata>{
            Name: "CleanupDocumentRecalc",
            RecalculateOnChange: ["$Nothing"],
            RecalculateWithParent: false,
            RecalculateToIgnore: ["ViewerUrl"],
            RecalculateDoNotSend: ["ViewerUrl"],
        };

        this.$metadataPropertyService.recalculateProperties(
            this.model.$$TypeName,
            propertyMetadata,
            this.model,
            this.model["$$Parent"],
            this.model[propertyMetadata.Name],
            undefined,
            true,
            true,
            this.datacontroller
        );
    }
}
